import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { useState } from 'react';
import { Loader2, Download, X } from 'lucide-react';
import { useAuditorExport } from './use-auditor-export';
import { AUDIT_TYPES, AuditorExportDialogProps } from './auditor-export.types.ts';
import { cn } from '@/lib/utils';

export function AuditorExportDialog({
    isOpen,
    onClose,
    companyID = window.companyID,
    onExportComplete,
    onError,
}: AuditorExportDialogProps) {
    const {
        isLoading,
        isExporting,
        error,
        frameworks,
        dateRange,
        auditInfo,
        toggleFramework,
        handleDateChange,
        handleAuditInfoChange,
        exportAuditPack,
        isSuccess,
    } = useAuditorExport(companyID);

    const [isProcessing, setIsProcessing] = useState(false);

    const handleExport = async () => {
        setIsProcessing(true);
        try {
            const result = await exportAuditPack();
            if (result && onExportComplete) {
                onExportComplete(result);
            } else if (!result && onError && error) {
                onError(error);
            }
        } finally {
            setIsProcessing(false);
        }
    };

    if (isLoading) {
        return (
            <Dialog open={isOpen} onOpenChange={onClose}>
                <DialogContent className="sm:max-w-[625px] bg-secondary">
                    <div className="flex items-center justify-center p-8">
                        <Loader2 className="h-8 w-8 animate-spin" />
                    </div>
                </DialogContent>
            </Dialog>
        );
    }

    if (isSuccess) {
        return (
            <Dialog open={isOpen} onOpenChange={onClose}>
                <DialogContent className="sm:max-w-[625px] bg-secondary">
                    <DialogHeader>
                        <DialogTitle>Export Successful</DialogTitle>
                        <DialogDescription>
                            Your auditor pack has been exported successfully.
                        </DialogDescription>
                    </DialogHeader>
                </DialogContent>
            </Dialog>
        );
    }

    if (error) {
        return (
            <Dialog open={isOpen} onOpenChange={onClose}>
                <DialogContent className="sm:max-w-[625px] bg-secondary">
                    <DialogHeader>
                        <DialogTitle>Error</DialogTitle>
                        <DialogDescription>
                            {error.message}
                        </DialogDescription>
                    </DialogHeader>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[625px] bg-secondary">
                <DialogHeader>
                    <DialogTitle>Export Auditor Pack</DialogTitle>
                    <DialogDescription>
                        Select the frameworks and date range for the export.
                    </DialogDescription>
                </DialogHeader>

                <Dialog open={isProcessing} onOpenChange={!isProcessing ? onClose : undefined}>
                    <DialogContent className="sm:max-w-[425px]">
                        <div className="flex flex-col items-center justify-center py-8">
                            <Loader2 className="h-12 w-12 animate-spin mb-4" />
                            <p className="text-lg font-medium">Exporting Auditor Pack...</p>
                            <p className="text-sm text-muted-foreground mt-2">This may take a moment</p>
                        </div>
                    </DialogContent>
                </Dialog>

                <div className="space-y-6 py-4">
                    {/* Frameworks Selection */}
                    <div>
                        <h3 className="mb-2 text-sm font-medium">Select Frameworks</h3>
                        <div className="grid grid-cols-2 gap-2">
                            {frameworks.map((framework) => (
                                <div key={framework.id} className="flex items-center space-x-2">
                                    <Checkbox
                                        id={framework.id}
                                        checked={framework.checked}
                                        onCheckedChange={() => toggleFramework(framework.id)}
                                    />
                                    <Label htmlFor={framework.id}>{framework.label}</Label>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Date Range */}
                    <div className="space-y-2">
                        <h3 className="text-sm font-medium">Assurance Tasks Date Range</h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1">
                                <Label htmlFor="startDate">Start Date</Label>
                                <Input
                                    id="startDate"
                                    type="date"
                                    value={dateRange.startDate}
                                    onChange={(e) => handleDateChange('startDate', e.target.value)}
                                />
                            </div>
                            <div className="space-y-1">
                                <Label htmlFor="endDate">End Date</Label>
                                <Input
                                    id="endDate"
                                    type="date"
                                    value={dateRange.endDate}
                                    onChange={(e) => handleDateChange('endDate', e.target.value)}
                                    min={dateRange.startDate}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Audit Info */}
                    <div className="space-y-4">
                        {/* <h3 className="text-sm font-medium">Audit Information</h3>

                        <div className="space-y-2">
                            <Label htmlFor="auditType">Type of Audit</Label>
                            <Select
                                value={auditInfo.typeOfAudit}
                                onValueChange={(value) => handleAuditInfoChange('typeOfAudit', value)}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select audit type" />
                                </SelectTrigger>
                                <SelectContent>
                                    
                                    {AUDIT_TYPES.map((type) => {
                                        console.log('[AuditPackDialog] type', type)
                                        return (
                                        <SelectItem
                                            key={type.value}
                                            value={type.value}
                                        >
                                            {type.label}
                                        </SelectItem>
                                    )})}
                                </SelectContent>
                            </Select>
                        </div> */}

                        {/* <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1">
                                <Label htmlFor="auditStartDate">Audit Start Date</Label>
                                <Input
                                    id="auditStartDate"
                                    type="date"
                                    value={auditInfo.startDate}
                                    onChange={(e) => handleAuditInfoChange('startDate', e.target.value)}
                                />
                            </div>
                            <div className="space-y-1">
                                <Label htmlFor="auditEndDate">Audit End Date</Label>
                                <Input
                                    id="auditEndDate"
                                    type="date"
                                    value={auditInfo.endDate}
                                    onChange={(e) => handleAuditInfoChange('endDate', e.target.value)}
                                    min={auditInfo.startDate}
                                />
                            </div>
                        </div> */}
                    </div>

                    {error && (
                        <div className="rounded-md bg-red-50 p-4 text-red-600 text-sm">
                            {error.message}
                        </div>
                    )}
                </div>

                <DialogFooter className="sm:justify-between">
                    <Button variant="outline" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleExport}
                        disabled={isExporting || frameworks.every((f) => !f.checked)}
                    >
                        {isExporting ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Exporting...
                            </>
                        ) : (
                            <>
                                <Download className="mr-2 h-4 w-4" />
                                Export Audit Pack
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
